/**
 * The markdown subset the concierge's answers use.
 *
 * A straight port of the phone's parser
 * (apps/mobile/src/features/discover/lib/doc-markdown.ts) so both surfaces
 * read the same document the same way. Deliberately hand-written rather than a
 * markdown library: the grammar is three constructs plus our own entity
 * marker, and unknown syntax has to degrade to literal text rather than throw.
 *
 * Grammar:
 *   ## heading
 *   - bullet
 *   **bold**
 *   [[slug|Label]]
 */
export type DocBlock =
  | { kind: "para"; text: string }
  | { kind: "heading"; text: string }
  | { kind: "bullet"; text: string };

export type InlineNode =
  | { kind: "text"; text: string }
  | { kind: "bold"; text: string }
  | { kind: "entity"; slug: string; text: string };

export function parseDoc(src: string): DocBlock[] {
  const out: DocBlock[] = [];
  for (const raw of src.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith("## ")) out.push({ kind: "heading", text: line.slice(3).trim() });
    else if (line.startsWith("- ")) out.push({ kind: "bullet", text: line.slice(2).trim() });
    else out.push({ kind: "para", text: line });
  }
  return out;
}

// Entity marker first so a label containing ** is not mis-read as bold.
const INLINE_RE = /\[\[([^|\]]+)\|([^\]]+)\]\]|\*\*([^*]+)\*\*/g;

export function parseInline(text: string): InlineNode[] {
  const out: InlineNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text))) {
    if (m.index > last) out.push({ kind: "text", text: text.slice(last, m.index) });
    if (m[1] != null) out.push({ kind: "entity", slug: m[1], text: m[2]! });
    else out.push({ kind: "bold", text: m[3]! });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  // An unclosed `**` or `[[` matches nothing above and falls through here as
  // literal text, which is the intended degradation.
  return out.length > 0 ? out : [{ kind: "text", text }];
}

/** True when the document has `##` sections to hang per-section cards off. */
export function hasSections(src: string): boolean {
  return parseDoc(src).some((b) => b.kind === "heading");
}

/**
 * The text carries document syntax, whatever the response said its format was.
 *
 * A belt to `format`'s braces: the shape of an answer arrives on ONE stream
 * frame, and while the web dropped that frame every markdown answer rendered
 * as plain text — which means it printed its own `[[slug|Label]]` and `**`
 * markers to the reader. Detecting the syntax makes that class of bug
 * unreachable rather than fixed once.
 */
export function looksLikeDoc(src: string): boolean {
  return /\[\[[^|\]]+\|[^\]]+\]\]|\*\*[^*]+\*\*/.test(src) || /(^|\n)\s*(##\s|-\s)/.test(src);
}
